import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import {
  getProductBasePath,
  getProductCatalog,
  findCatalogEntryByKind,
  parseProductDetailPath,
  ProductCatalogEntry,
  ProductKind,
  resolveProductEntry
} from "../config/product-catalog";
import { SiilaTranslationService } from "./siila-translation.service";

/** One ready-to-render product link: the header menu, the sidenav and the
 * footer all consume this shape, so the three cannot present different sets. */
export interface ProductNavLink {
  /** Localized, `/lang`-suffixed path — hand straight to `routerLink`/`href`. */
  path: string;
  /** i18n key for the label. */
  labelKey: string;
  /** Material icon name (menu/sidenav only; the footer ignores it). */
  icon: string;
  /** Casing class for the anchor. */
  linkClass: string;
  kind: ProductKind;
}

/**
 * The one way to turn `product-catalog.ts` data into URLs.
 *
 * Every product link in the app comes from here: the header dropdown, the
 * sidenav, the footer, the Market Analytics cards, and the canonical/hreflang
 * tags on the detail page itself. Paths are composed by
 * `SiilaTranslationService.buildLocalizedPath`, so the `/lang/<code>` suffix
 * follows the same default-language rule as every other internal link.
 */
@Injectable({ providedIn: "root" })
export class ProductCatalogService {
  private readonly platform: string = environment.platformId;

  constructor(private siilaTranslationService: SiilaTranslationService) {}

  /** Raw entries for this deployment, in render order. */
  getEntries(): ProductCatalogEntry[] {
    return getProductCatalog(this.platform);
  }

  /**
   * The deployment's product links, localized to `lang` (the active language by
   * default). Fresh objects on every call — callers historically mutated the
   * shared footer arrays in place, which leaked one page's language into the next.
   */
  getNavLinks(lang?: string): ProductNavLink[] {
    const language = lang || this.siilaTranslationService.getCurrentLanguage();
    return this.getEntries().map(entry => ({
      path: this.buildPath(entry, language),
      labelKey: entry.labelKey,
      icon: entry.icon,
      linkClass: entry.linkClass,
      kind: entry.kind
    }));
  }

  /** Localized path for one entry — leading slash, `/lang` suffix applied. */
  buildPath(entry: ProductCatalogEntry, lang?: string): string {
    const language = lang || this.siilaTranslationService.getCurrentLanguage();
    const base = getProductBasePath(entry, language);
    return base
      ? this.siilaTranslationService.buildLocalizedPath("/" + base, language)
      : null;
  }

  /**
   * Localized path for whichever entry currently answers `id` on this
   * deployment, or `null` when none does. `slug` disambiguates the handful of
   * ids that moved between products (see `PRODUCT_REDIRECTS`); omit it when the
   * caller only has an id, e.g. a CMS-driven Market Analytics card.
   */
  buildPathForId(id: number, slug: string = "", lang?: string): string {
    const entry = resolveProductEntry(this.platform, id, slug);
    return entry ? this.buildPath(entry, lang) : null;
  }

  /** Localized path for the first entry of `kind`, or `null` if not published here. */
  buildPathForKind(kind: ProductKind, lang?: string): string {
    const entry = findCatalogEntryByKind(this.platform, kind);
    return entry ? this.buildPath(entry, lang) : null;
  }

  /**
   * Resolve a requested `products/:id/:name` route.
   *
   * Returns the entry that should render plus its ONE canonical path, or `null`
   * when this deployment publishes no such product — which is the detail page's
   * cue to answer 404 rather than render an empty shell. `canonicalPath` already
   * accounts for both corrections the catalog knows about: a stale slug on a
   * live id, and an id that moved to another product.
   */
  resolveRoute(
    rawId: string,
    rawSlug: string,
    lang?: string
  ): { entry: ProductCatalogEntry; canonicalPath: string } {
    const entry = resolveProductEntry(this.platform, +rawId, rawSlug);
    if (!entry) {
      return null;
    }
    return { entry, canonicalPath: this.buildPath(entry, lang) };
  }

  /**
   * Suffixless base path (`products/<id>/<slug>`, no leading slash) for one
   * entry in one language — the shape `MetaTagService.addHreflangTags` expects
   * from its per-language builder.
   */
  buildBasePath(entry: ProductCatalogEntry, lang: string): string {
    return getProductBasePath(entry, lang);
  }

  /**
   * What kind of product a `/products/...` path addresses, or `null` when the
   * path is not one this deployment publishes. Lets meta-tag dispatch key off
   * the product instead of matching translated slugs (`índices` vs `indices`).
   */
  getKindForPath(path: string): ProductKind {
    const parsed = parseProductDetailPath(path);
    if (!parsed) {
      return null;
    }
    const entry = resolveProductEntry(this.platform, parsed.id, parsed.slug);
    return entry ? entry.kind : null;
  }
}
